/**
 * <ActivationComposerSkeleton> — la forme du composeur de la reprise pendant son chargement.
 *
 * Les sections de `<ActivationComposerTemplate>` dans leur ordre, avec leurs vrais intitulés et
 * leurs glyphes — ils ne dépendent d'aucune donnée : la classe en pilules (la boîte d'un poussoir
 * `sm`), les heures en cartes d'option (un palier « Le plus choisi » y passe toute la rangée), les
 * matières et les créneaux dans leur carte à chevron, la date de reprise en pilules, les modes de
 * cours en cartes d'option. Le reste du formulaire est sous la ligne de flottaison d'un
 * téléphone ; le bouton d'envoi, lui, est déjà là : c'est le pied fixe de la page.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { type IconName } from "../../atoms/icons/Icon"
import { Card } from "../../molecules/Card"
import { List } from "../../molecules/List"
import { OptionCardSkeleton } from "../../molecules/OptionCard/OptionCardSkeleton"
import { OPTION_MIN_WIDTH } from "../../organisms/ActivationChoiceSection/ActivationChoiceSection"

/** Une pilule de sélection est une face de poussoir `sm` (`<ChoiceChip variant="pill">`). */
const PILL_HEIGHT = pushButtonBoxHeight("sm")

function PillsBones(props: { widths: number[] }) {
    return (
        <View style={styles.pills}>
            {props.widths.map((width, index) => (
                <Skeleton key={index} shape="block" width={width} height={PILL_HEIGHT} radius="md" />
            ))}
        </View>
    )
}

/** La carte à chevron d'un choix fait ailleurs (`<ActivationPickerSection>`). */
function PickerBones(props: { title: string; iconName: IconName; width: number }) {
    return (
        <List title={props.title} titleIcon={props.iconName}>
            <Card p="3">
                <Row gap="3" align="center">
                    <View style={styles.fill}>
                        <Skeleton width={props.width} />
                    </View>
                    <View style={styles.chevron} />
                </Row>
            </Card>
        </List>
    )
}

export function ActivationComposerSkeleton() {
    return (
        <Stack gap="6">
            <List title="Classe à la rentrée" titleIcon="graduation-cap">
                <PillsBones widths={[50, 50, 53, 58, 58, 58, 54, 55, 51, 45, 59]} />
            </List>
            <List title="Heures par semaine" titleIcon="clock">
                {/* Un palier porte « Le plus choisi » : toute la rangée passe en cartes d'option. */}
                <View style={styles.pills}>
                    {[0, 1, 2, 3, 4].map((index) => (
                        <View key={index} style={styles.hoursOption}>
                            <OptionCardSkeleton labelWidth={28} descriptionWidth={null} />
                        </View>
                    ))}
                </View>
            </List>
            <PickerBones title="Matières souhaitées" iconName="book" width={150} />
            <PickerBones title="Créneaux" iconName="clock" width={130} />
            <List title="Date de reprise" titleIcon="calendar-alt">
                <PillsBones widths={[118, 118, 122]} />
            </List>
            <List title="Mode de cours" titleIcon="users">
                <Stack gap="2">
                    <OptionCardSkeleton labelWidth={140} descriptionWidth={210} />
                    <OptionCardSkeleton labelWidth={120} descriptionWidth={190} />
                </Stack>
            </List>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    pills: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    /** La largeur d'une option de la rangée (`OPTION_MIN_WIDTH` de la section). */
    hoursOption: { minWidth: OPTION_MIN_WIDTH },
    fill: { flexGrow: 1, flexShrink: 1 },
    /** La place du chevron (`<Icon size={16}>`). */
    chevron: { width: 16, height: 16 },
}))
